import { ReactNode, useState, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDisabilityCertificateList } from "@/services/disabilityCertificate";
import { Module } from "@/constants";
import i18n from "@/i18n";
import {
  QuestionnaireContext,
  QuestionnaireContextType,
  QuestionnaireStep,
} from "./QuestionnaireContext";
import {
  useQuestionnaireHandlers,
  useQuestionnaireLoadingState,
  useQuestionnaireError,
  useQuestionnaireDialog,
  usePersistedQuestionnaireDraft,
} from "../hooks";
import { QuestionnaireConfirmDialog } from "../components/shared/QuestionnaireConfirmDialog";
import { getQuestionnaire } from "@/services/questionnaire";
import { usePresignedClient } from "@/lib/file-storage/hooks";
import { buildQuestionnairePresignUrl } from "../utils";

interface QuestionnaireProviderProps {
  children: ReactNode;
  token: string;
}

export const QuestionnaireProvider = ({
  children,
  token,
}: QuestionnaireProviderProps) => {
  const [currentStep, setCurrentStep] = useState<QuestionnaireStep>("form");
  const [isAccepted, setIsAccepted] = useState(false);
  const [summaryPdfBase64, setSummaryPdfBase64] = useState<string | null>(null);

  useEffect(() => {
    i18n.changeLanguage("pl");
  }, []);

  const questionnaireQuery = useQuery({
    queryKey: ["questionnaire", token],
    queryFn: () => getQuestionnaire(token),
    enabled: Boolean(token),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const disabilityCertificateQuery = useQuery({
    queryKey: [Module.DisabilityCertificates],
    queryFn: () => fetchDisabilityCertificateList(),
    enabled: Boolean(questionnaireQuery.data),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const { persistedFormData, setPersistedFormData } =
    usePersistedQuestionnaireDraft(token, questionnaireQuery.data);

  const { data } = questionnaireQuery;

  const agreementId = data?.questionnaire?.agreement_id;
  const agreementStatus = data?.questionnaire?.agreement_status;

  const presignedClient = usePresignedClient({
    presignUrl: buildQuestionnairePresignUrl(agreementId, token),
    completeUrl: buildQuestionnairePresignUrl(agreementId, token, "complete"),
    completeDeleteUrl: buildQuestionnairePresignUrl(
      agreementId,
      token,
      "complete-delete",
    ),
  });

  const {
    dialogAction,
    dialogWarningDescription,
    openDialog,
    closeDialog,
    confirmDialog,
  } = useQuestionnaireDialog();

  const {
    mutations,
    handleSubmitForm,
    handleSubmitFormWithoutConfirm,
    handleAcceptAgreement,
    handleCancelAgreement,
  } = useQuestionnaireHandlers({
    agreementId,
    token,
    agreementStatus,
    setCurrentStep,
    setIsAccepted,
    setSummaryPdfBase64,
    openDialog,
    closeDialog,
    setPersistedFormData,
    questionnaire: data?.questionnaire,
  });

  const queries = { questionnaireQuery, disabilityCertificateQuery };

  const loadingState = useQuestionnaireLoadingState(queries, mutations);

  const { errorState, clearError, shouldShowAlert, alertReason } =
    useQuestionnaireError(queries, mutations);

  const refreshQuestionnaire = useCallback(() => {
    questionnaireQuery.refetch();
  }, [questionnaireQuery]);

  const contextValue: QuestionnaireContextType = useMemo(
    () => ({
      token,
      questionnaireQuery,
      disabilityCertificateQuery,
      presignedClient,
      currentStep,
      isAccepted,
      summaryPdfBase64,
      persistedFormData,
      loadingState,
      errorState,
      shouldShowAlert,
      alertReason,
      handleSubmitForm,
      handleSubmitFormWithoutConfirm,
      handleAcceptAgreement,
      handleCancelAgreement,
      clearError,
      refreshQuestionnaire,
    }),
    [
      token,
      questionnaireQuery,
      disabilityCertificateQuery,
      presignedClient,
      currentStep,
      isAccepted,
      summaryPdfBase64,
      persistedFormData,
      loadingState,
      errorState,
      shouldShowAlert,
      alertReason,
      handleSubmitForm,
      handleSubmitFormWithoutConfirm,
      handleAcceptAgreement,
      handleCancelAgreement,
      clearError,
      refreshQuestionnaire,
    ],
  );

  return (
    <QuestionnaireContext.Provider value={contextValue}>
      {children}
      <QuestionnaireConfirmDialog
        handlerKey={dialogAction}
        warningDescription={dialogWarningDescription}
        onConfirm={confirmDialog}
        onCancel={closeDialog}
      />
    </QuestionnaireContext.Provider>
  );
};
